/**
 * Utilities for transforming API data to the format expected by reporting components
 */
import type { ChartSpec } from 'src/components/charts';
import type {
  BoundaryDirection,
  CalculatedMetricNode,
  CardChartType,
  Level,
  LevelCalculationMode,
  UpdateCadence
} from '../api/types';
import type { AssuranceLevel } from './color';

// Map backend Level enum to frontend AssuranceLevel
export function mapLevelToAssurance(level: Level | null): AssuranceLevel {
  if (!level) return 'NaN';
  const mapping: Record<Level, AssuranceLevel> = {
    high: 'High',
    substantial: 'Substantial',
    moderate: 'Moderate',
    limited: 'Limited',
    none: 'None',
    nan: 'NaN'
  };
  return mapping[level] ?? 'NaN';
}

// Get suffix based on data value type
export function getSuffix(dataValueType: string | null): string {
  if (dataValueType === 'percentage') return '%';
  return '';
}

export interface ProcessedNode {
  id: string;
  name: string;
  value: number | null;
  assurance: AssuranceLevel;
  trend: 'up' | 'down' | 'static';
  sparkline: (number | null)[];
  suffix: string;
  children: ProcessedNode[];
  type: 'data_card' | 'group_card' | 'status_card';
  previousAssuranceLevel: AssuranceLevel | null;
  historicalAssuranceLevels: (AssuranceLevel | null)[];
  updateCadence: UpdateCadence | null;
  chartType: CardChartType | null;
  aggregationType: 'min' | 'average' | null;
  linkedNodeId: string | null;
  linkedNodeDashboardId: string | null;
  hasLinkedCardAccess: boolean | null;
  groupNumericDisplayValue: number | null;
  groupNumericDisplayTooltip: string | null;
  isPinned: boolean;
  chartSpec: ChartSpec | null;
}

export function calculateTrend(
  current: number | null,
  previous: number | null,
  boundaryDirection?: BoundaryDirection | null,
  levelCalculationMode?: LevelCalculationMode | null,
  baseValue?: number | null
): 'up' | 'down' | 'static' {
  if (current === null || previous === null) return 'static';
  const delta = current - previous;
  if (Math.abs(delta) < 0.5) return 'static';

  // For deviation_bands, "up" means moving closer to the base (the target),
  // which is an improvement from either side. Scores are raw values, so measure
  // each one's distance from the base. Only the current period's base is known,
  // so a monthly base is approximated by it for the previous period too.
  if (levelCalculationMode === 'deviation_bands') {
    if (baseValue == null) return 'static';
    const currentDistance = Math.abs(current - baseValue);
    const previousDistance = Math.abs(previous - baseValue);
    if (currentDistance < previousDistance - 0.5) return 'up';
    if (currentDistance > previousDistance + 0.5) return 'down';
    return 'static';
  }

  // For ascending (lower-is-better) metrics — defect counts, incident rates,
  // error % — an increase is a worsening trend. Flip up/down to match.
  if (boundaryDirection === 'ascending') {
    return delta > 0 ? 'down' : 'up';
  }

  // Default: descending / higher-is-better — increase is improvement.
  return delta > 0 ? 'up' : 'down';
}

export function processNodeForDisplay(node: CalculatedMetricNode): ProcessedNode {
  // Status cards reuse raw_value (1.0 = Yes, 0.0 = No) but have no numeric score.
  // Data cards use calculated_score or fall back to raw_value.
  const displayValue =
    node.type === 'data_card'
      ? (node.calculated_score ?? node.raw_value)
      : node.type === 'status_card'
        ? node.raw_value
        : null;
  const trend = calculateTrend(
    displayValue,
    node.previous_period_score,
    node.boundary_direction,
    node.level_calculation_mode,
    node.base_value_used
  );
  const sparkline =
    node.type === 'status_card'
      ? []
      : node.historical_scores && node.historical_scores.length > 0
        ? node.historical_scores
        : displayValue !== null
          ? [displayValue]
          : [];

  const historicalAssuranceLevels = (node.historical_assurance_levels || []).map((level) => mapLevelToAssurance(level));
  const previousAssuranceLevel = mapLevelToAssurance(node.previous_assurance_level);

  return {
    id: node.id,
    name: node.title,
    value: displayValue,
    assurance: mapLevelToAssurance(node.assurance_level),
    trend,
    sparkline,
    suffix: getSuffix(node.data_value_type),
    children: node.children.map(processNodeForDisplay),
    type: node.type,
    previousAssuranceLevel: previousAssuranceLevel === 'NaN' ? null : previousAssuranceLevel,
    historicalAssuranceLevels,
    updateCadence: node.update_cadence,
    chartType: node.chart_type,
    aggregationType: node.aggregation_type,
    linkedNodeId: node.linked_node_id,
    linkedNodeDashboardId: node.linked_node_dashboard_id,
    hasLinkedCardAccess: node.has_linked_card_access,
    groupNumericDisplayValue: node.group_numeric_display_value ?? null,
    groupNumericDisplayTooltip: (() => {
      if (node.group_numeric_display_value == null) return null;
      switch (node.group_numeric_display_type) {
        case 'assurance_score':
          return "Numeric rank of this group's assurance level (1 = None → 5 = High)";
        case 'child_score_aggregate': {
          const aggLabels: Record<string, string> = { average: 'Average', sum: 'Sum', min: 'Minimum', max: 'Maximum' };
          const label = aggLabels[node.group_numeric_child_aggregation ?? 'average'] ?? 'Aggregate';
          return `${label} of direct child card values`;
        }
        case 'pinned_child': {
          const pinned = node.children.find((c) => c.id === node.pinned_child_id);
          return pinned ? `Value from: ${pinned.title}` : 'Value from selected child card';
        }
        default:
          return null;
      }
    })(),
    isPinned: node.is_pinned,
    chartSpec: (node.chart_spec ?? null) as ChartSpec | null
  };
}

export function findNodeById(nodes: ProcessedNode[], id: string): ProcessedNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNodeById(node.children, id);
    if (found) return found;
  }
  return null;
}
